import { Injectable, inject } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { Database } from 'supabase/types/supabase';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number; // in cents
  priceFormatted: string;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  balance_after: number;
  transaction_type: 'purchase' | 'usage' | 'refund' | 'bonus';
  description: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class CreditsService {
  private supabaseClient = inject<SupabaseClient<Database>>(SupabaseClient);

  private balanceSubject = new BehaviorSubject<number>(0);
  readonly balance$: Observable<number> = this.balanceSubject.asObservable();

  private packagesCache: CreditPackage[] | null = null;

  /**
   * Get the current credit balance for the authenticated user
   */
  async getBalance(): Promise<number> {
    const {
      data: { user },
    } = await this.supabaseClient.auth.getUser();
    if (!user) {
      return 0;
    }

    // Use type assertion since the RPC is defined in migration but not yet in generated types
    const { data, error } = await (this.supabaseClient.rpc as CallableFunction)(
      'get_credit_balance',
      { user_uuid: user.id }
    );

    if (error) {
      console.error('Failed to get credit balance:', error);
      return 0;
    }

    const balance = (data as number) || 0;
    this.balanceSubject.next(balance);
    return balance;
  }

  /**
   * Check if the user has sufficient credits
   */
  async hasCredits(amount: number = 1): Promise<boolean> {
    const balance = await this.getBalance();
    return balance >= amount;
  }

  /**
   * Get credit transaction history
   * Note: This will fail until the migration is applied
   */
  async getTransactionHistory(
    limit: number = 50
  ): Promise<CreditTransaction[]> {
    // Use type assertion since the table is defined in migration but not yet in generated types
    const client = this.supabaseClient;
    const { data, error } = await client
      .from('credit_transactions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get transaction history:', error);
      return [];
    }

    return data as CreditTransaction[];
  }

  /**
   * Get available credit packages from database
   */
  async getPackages(): Promise<CreditPackage[]> {
    // Return cached packages if available
    if (this.packagesCache) {
      return this.packagesCache;
    }

    const client = this.supabaseClient;
    const { data, error } = await client
      .from('credit_packages')
      .select('id, name, credits, price_cents')
      .eq('active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Failed to get credit packages:', error);
      return [];
    }

    // Transform to CreditPackage format
    this.packagesCache = (
      data as Array<{
        id: string;
        name: string;
        credits: number;
        price_cents: number;
      }>
    ).map((pkg) => ({
      id: pkg.id,
      name: pkg.name,
      credits: pkg.credits,
      price: pkg.price_cents,
      priceFormatted: formatPrice(pkg.price_cents),
    }));

    return this.packagesCache;
  }

  /**
   * Refresh the balance (call after a purchase or usage)
   */
  async refreshBalance(): Promise<number> {
    return this.getBalance();
  }

  /**
   * Get current cached balance (synchronous)
   */
  getCurrentBalance(): number {
    return this.balanceSubject.value;
  }

  /**
   * Purchase a credit package.
   * Opens Stripe Checkout and handles the return flow.
   * - Native: Opens in SFSafariViewController, refreshes balance when browser closes
   * - Web: Redirects to Stripe, returns to /account page with success toast
   * @returns The Stripe Checkout URL (for web redirect) or undefined (for native where browser is opened)
   */
  async purchasePackage(packageId: string): Promise<string | undefined> {
    const {
      data: { user },
    } = await this.supabaseClient.auth.getUser();
    if (!user) {
      throw new Error('Must be logged in to purchase credits');
    }

    const fromApp = Capacitor.isNativePlatform();

    const { data, error } = await this.supabaseClient.functions.invoke<{
      url: string;
    }>('checkout', {
      body: {
        packageId,
        fromApp,
      },
    });

    if (error) {
      throw error;
    }

    if (!data?.url) {
      throw new Error('No checkout URL returned');
    }

    if (Capacitor.isNativePlatform()) {
      // Open Stripe Checkout in SFSafariViewController
      await Browser.open({ url: data.url });

      // Listen for browser close and refresh balance
      const listener = await Browser.addListener(
        'browserFinished',
        async () => {
          await listener.remove();
          await this.refreshBalance();
        }
      );

      return undefined;
    }

    // Web flow: return URL for caller to redirect
    return data.url;
  }
}
